import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";
import { CompassLoader } from "@/components/compass-loader";
import { isStaleChunkError, attemptStaleChunkReload } from "@/lib/stale-chunk";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Portal attribution surfaced as a Sentry tag on captured errors. */
  portal?: "admin" | "agent" | "investor" | "public";
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** The caught error looks like a failed dynamic import of an outdated chunk. */
  isStaleChunk: boolean;
  /** A stale-chunk reload was attempted but suppressed by the loop guard. */
  reloadFailed: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, isStaleChunk: false, reloadFailed: false };

  static getDerivedStateFromError(error: Error): State {
    // Detect the stale-chunk case during the render phase so we can show a
    // neutral "updating" loader instead of flashing the maintenance copy before
    // componentDidCatch runs. The actual reload side effect happens there.
    return {
      hasError: true,
      error,
      isStaleChunk: isStaleChunkError(error),
      reloadFailed: false,
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isStaleChunkError(error)) {
      // The app was updated underneath this tab. Recover with a one-time,
      // loop-guarded reload rather than reporting a hard crash. Successful
      // reloads are expected post-deploy noise — do not create a Sentry issue.
      // Only report when the loop guard blocks reload (user stuck on maintenance).
      const reloaded = attemptStaleChunkReload();
      if (!reloaded) {
        Sentry.captureMessage("stale_chunk_auto_reload", {
          level: "warning",
          tags: {
            kind: "stale_chunk_reload",
            reloaded: "false",
            ...(this.props.portal ? { portal: this.props.portal } : {}),
          },
        });
        // Reload was suppressed (loop guard / no sessionStorage) — fall through
        // to the maintenance screen instead of refreshing forever.
        this.setState({ reloadFailed: true });
      }
      return;
    }

    Sentry.captureException(error, {
      tags: {
        kind: "react_error_boundary",
        ...(this.props.portal ? { portal: this.props.portal } : {}),
      },
      contexts: {
        react: { componentStack: info.componentStack ?? null },
      },
    });
  }

  handleReset = () => {
    // A real reload of the current route (not just an in-memory state reset) so
    // a stale-chunk crash on the CURRENT page recovers too — resetting state
    // would only re-attempt the exact same missing chunk and fail again.
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Stale-chunk recovery in progress: a full reload is underway (or about to
      // be). Show a calm loader, never the scary maintenance copy.
      if (this.state.isStaleChunk && !this.state.reloadFailed) {
        return <CompassLoader variant="inline" />;
      }

      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-[50vh] flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <h3 className="text-lg font-heading font-semibold mb-2">We're doing some maintenance</h3>
            <p className="text-sm text-muted-foreground mb-6">
              We're making a few improvements to this section and will be back shortly. Please check back later.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={this.handleReset} className="gap-2" data-testid="button-error-reset">
                <RotateCcw className="w-4 h-4" />
                Try Again
              </Button>
              <Button variant="outline" onClick={() => window.location.href = "/"} className="gap-2" data-testid="button-error-home">
                <Home className="w-4 h-4" />
                Go Home
              </Button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-muted-foreground cursor-pointer">Error details</summary>
                <pre className="mt-2 text-xs text-muted-foreground bg-muted p-3 rounded-lg overflow-auto max-h-32 border">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
